import { PASSWORD_MIN_LENGTH, passwordChangeRequestSchema, type CurrentUser } from '@mfos/shared';
import { type SubmitEvent, useState } from 'react';
import { Link } from 'react-router';
import { ErrorNotice, SuccessNotice } from '../../components/ErrorNotice';
import { TextField } from '../../components/Field';
import { validate, type FieldErrors } from '../../lib/validation';
import { useChangePassword } from '../auth/auth.api';
import { phrase } from '../../lib/i18n/runtime';

export function PasswordSection({ user }: { user: CurrentUser }) {
  return (
    <section className="card stack" aria-labelledby="password-heading">
      <h2 id="password-heading">{phrase('Password')}</h2>
      {user.hasPassword ? (
        <ChangePasswordForm />
      ) : (
        <p>
          Your account has no password yet — you sign in with email links. To set one, use{' '}
          <Link to="/forgot-password">{phrase('reset password')}</Link>{' '}
          {phrase('and follow the link we email you.')}
        </p>
      )}
    </section>
  );
}

function ChangePasswordForm() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const mutation = useChangePassword();

  function submit(event: SubmitEvent) {
    event.preventDefault();
    const result = validate(passwordChangeRequestSchema, { currentPassword, newPassword });
    const nextErrors: Record<string, string> = result.ok ? {} : { ...result.errors };
    if (nextErrors.currentPassword !== undefined) {
      nextErrors.currentPassword = 'Enter your current password.';
    }
    if (confirmation !== newPassword) {
      nextErrors.confirmation = 'The new passwords do not match.';
    }
    setErrors(nextErrors);
    if (result.ok && Object.keys(nextErrors).length === 0) {
      mutation.mutate(result.data, {
        onSuccess: () => {
          setCurrentPassword('');
          setNewPassword('');
          setConfirmation('');
        },
      });
    }
  }

  return (
    <form onSubmit={submit} noValidate className="stack">
      <TextField
        label={phrase('Current password')}
        type="password"
        autoComplete="current-password"
        value={currentPassword}
        onValueChange={setCurrentPassword}
        error={errors.currentPassword}
        required
      />
      <TextField
        label={phrase('New password')}
        type="password"
        autoComplete="new-password"
        value={newPassword}
        onValueChange={setNewPassword}
        error={errors.newPassword}
        hint={`At least ${String(PASSWORD_MIN_LENGTH)} characters. A long passphrase works well.`}
        required
      />
      <TextField
        label={phrase('Confirm new password')}
        type="password"
        autoComplete="new-password"
        value={confirmation}
        onValueChange={setConfirmation}
        error={errors.confirmation}
        required
      />
      <ErrorNotice error={mutation.error} />
      {mutation.isSuccess && (
        <SuccessNotice>
          {phrase('Password changed. Your other sessions were signed out.')}
        </SuccessNotice>
      )}
      <div className="form-actions">
        <button type="submit" className="button" disabled={mutation.isPending}>
          {mutation.isPending ? phrase('Changing…') : phrase('Change password')}
        </button>
      </div>
    </form>
  );
}
